import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import '../styles/TaskBoardManager.css';
import Layout from '../components/Layout';
import TaskBoardList from '../components/TaskBoardList';
import TaskBoard from './TaskBoard';
import { SavedTaskBoard, taskboardService, Column } from '../services/taskboardService';
import { projectStore } from '../lib/projectStore';

const COLUMN_COLORS = [
  '#6366f1','#0ea5e9','#f59e0b','#10b981','#ec4899',
  '#8b5cf6','#ef4444','#14b8a6','#f97316','#84cc16',
];

interface NewColumnDef { id: string; title: string; color: string; }

const TaskBoardManager: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<'list' | 'board'>('list');
  const [selectedTaskBoard, setSelectedTaskBoard] = useState<SavedTaskBoard | null>(null);
  const [taskToOpen, setTaskToOpen] = useState<{ taskId: string; columnId: string } | null>(null);

  // Column setup modal state
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [boardName, setBoardName] = useState('');
  const [newColumns, setNewColumns] = useState<NewColumnDef[]>([
    { id: crypto.randomUUID(), title: 'Backlog', color: '#0ea5e9' },
    { id: crypto.randomUUID(), title: 'To Do', color: '#f59e0b' },
    { id: crypto.randomUUID(), title: 'In Progress', color: '#8b5cf6' },
    { id: crypto.randomUUID(), title: 'Completed', color: '#10b981' },
  ]);
  const [creating, setCreating] = useState(false);

  const handleSelectTaskBoard = (taskboard: SavedTaskBoard) => {
    setSelectedTaskBoard(taskboard);
    setCurrentView('board');
    navigate(`/taskboard/${taskboard.id}`, { replace: false });
  };

  const handleTaskClick = (taskboard: SavedTaskBoard, taskId: string, columnId: string) => {
    setSelectedTaskBoard(taskboard);
    setTaskToOpen({ taskId, columnId });
    setCurrentView('board');
    navigate(`/taskboard/${taskboard.id}`, { replace: false });
  };

  const openSetupModal = () => {
    setBoardName('');
    setNewColumns([
      { id: crypto.randomUUID(), title: 'Backlog', color: '#0ea5e9' },
      { id: crypto.randomUUID(), title: 'To Do', color: '#f59e0b' },
      { id: crypto.randomUUID(), title: 'In Progress', color: '#8b5cf6' },
      { id: crypto.randomUUID(), title: 'Completed', color: '#10b981' },
    ]);
    setShowSetupModal(true);
  };

  const addColumn = () => {
    if (newColumns.length >= 10) return;
    const colorIdx = newColumns.length % COLUMN_COLORS.length;
    setNewColumns(prev => [...prev, { id: crypto.randomUUID(), title: `Column ${prev.length + 1}`, color: COLUMN_COLORS[colorIdx] }]);
  };

  const removeColumn = (colId: string) => {
    if (newColumns.length <= 1) return;
    setNewColumns(prev => prev.filter(c => c.id !== colId));
  };

  const updateColumn = (colId: string, field: 'title' | 'color', value: string) => {
    setNewColumns(prev => prev.map(c => c.id === colId ? { ...c, [field]: value } : c));
  };

  const handleCreateBoard = async () => {
    if (!boardName.trim()) { alert('Board name is required'); return; }
    const validCols = newColumns.filter(c => c.title.trim());
    if (validCols.length === 0) { alert('At least one column is required'); return; }

    try {
      setCreating(true);
      const columns: Column[] = validCols.map(c => ({
        id: c.id,
        title: c.title.trim(),
        color: c.color,
        tasks: [],
      }));
      const newBoard = await taskboardService.createTaskBoard(boardName.trim(), columns, projectStore.getProjectId());
      setShowSetupModal(false);
      setSelectedTaskBoard(newBoard);
      setCurrentView('board');
      navigate(`/taskboard/${newBoard.id}`, { replace: false });
    } catch (error) {
      console.error('Error creating new task board:', error);
      alert('Failed to create task board');
    } finally {
      setCreating(false);
    }
  };

  const handleBackToList = () => {
    setSelectedTaskBoard(null);
    setCurrentView('list');
    setTaskToOpen(null);
    navigate('/taskboards', { replace: false });
  };

  useEffect(() => {
    if (!id) return;
    taskboardService.getTaskBoard(id).then(taskboard => {
      if (taskboard) {
        setSelectedTaskBoard(taskboard);
        setCurrentView('board');
        const taskId = searchParams.get('task');
        const columnId = searchParams.get('column');
        if (taskId && columnId) setTaskToOpen({ taskId, columnId });
      } else {
        navigate('/taskboards', { replace: true });
      }
    }).catch(() => navigate('/taskboards', { replace: true }));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      {currentView === 'list' ? (
        <TaskBoardList
          onSelectTaskBoard={handleSelectTaskBoard}
          onNewBoard={openSetupModal}
          onTaskClick={handleTaskClick}
        />
      ) : (
        <TaskBoard
          taskboard={selectedTaskBoard}
          onBackToList={handleBackToList}
          onUpdate={(updatedBoard: SavedTaskBoard) => setSelectedTaskBoard(updatedBoard)}
          taskToOpen={taskToOpen}
          onTaskOpened={() => setTaskToOpen(null)}
        />
      )}

      {/* ── New Board Setup Modal ────────────────────────────────── */}
      {showSetupModal && (
        <div className="tbm-modal-backdrop" onClick={() => !creating && setShowSetupModal(false)}>
          <div className="tbm-modal" onClick={e => e.stopPropagation()}>
            <div className="tbm-modal-header">
              <h2>Create New Task Board</h2>
              <button className="tbm-close-btn" onClick={() => setShowSetupModal(false)} disabled={creating}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Board name */}
            <div className="tbm-field">
              <label className="tbm-label">Board Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                className="tbm-input"
                type="text"
                placeholder="e.g. Engineering Sprint Q3"
                value={boardName}
                onChange={e => setBoardName(e.target.value)}
                autoFocus
              />
            </div>

            {/* Columns */}
            <div className="tbm-field">
              <div className="tbm-cols-header">
                <label className="tbm-label">Columns <span className="tbm-col-count">({newColumns.length}/10)</span></label>
                {newColumns.length < 10 && (
                  <button className="tbm-add-col-btn" type="button" onClick={addColumn}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add Column
                  </button>
                )}
              </div>

              <div className="tbm-cols-list">
                {newColumns.map((col, idx) => (
                  <div key={col.id} className="tbm-col-row">
                    <span className="tbm-col-num">{idx + 1}</span>
                    {/* Colour picker dot */}
                    <div className="tbm-color-wrap">
                      <input
                        type="color"
                        className="tbm-color-input"
                        value={col.color}
                        onChange={e => updateColumn(col.id, 'color', e.target.value)}
                        title="Column colour"
                      />
                      <div className="tbm-color-dot" style={{ background: col.color }} />
                    </div>
                    <input
                      className="tbm-col-name-input"
                      type="text"
                      placeholder={`Column ${idx + 1}`}
                      value={col.title}
                      onChange={e => updateColumn(col.id, 'title', e.target.value)}
                    />
                    <button
                      className="tbm-remove-col-btn"
                      type="button"
                      onClick={() => removeColumn(col.id)}
                      disabled={newColumns.length <= 1}
                      title="Remove column"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="tbm-modal-footer">
              <button className="tbm-cancel-btn" onClick={() => setShowSetupModal(false)} disabled={creating}>Cancel</button>
              <button className="tbm-create-btn" onClick={handleCreateBoard} disabled={creating || !boardName.trim()}>
                {creating ? 'Creating...' : 'Create Board'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default TaskBoardManager;
